import { Col, Container, Form, Row } from 'react-bootstrap'
import React, { useState } from 'react'
import './../Settings.scss'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { Button } from '../../../../components/Button/Button'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useAccount } from '../../../../common/useAccount'
import { SelectDropdown } from '../../../../components/SelectDropdown/SelectDropdown'
import { MeetingLanguage } from '../../../../models/Room'
import { useLanguageOptions } from '../../../../common/languageUtils'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { LanguageSettingModal } from './LanguageSettingModal'
import { DisableLanguageSettingModal } from './DisableLanguageSettingModal'
import { useMultiLanguageEnabled } from '../../../../common/useMultiLanguageEnabled'
import { SummaryToast } from '../../../../apps/professional-app/fa/MeetingSummary/Common/SummaryToast'
import { EnterpriseManagedBanner } from '../EnterpriseManagedBanner'

export const LanguageSettings: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { account, isFetching: isAccountFetching } = useAccount({ refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: isAccountLoading }] = useUpdateAccountMutation()

  const { multiLanguageEnabled, defaultLanguage } = useMultiLanguageEnabled()
  const [multiLanguageSupportEnabled, setMultiLanguageSupportEnabled] = useState<boolean>(multiLanguageEnabled)
  const [multiLanguageSupportDefaultLanguage, setMultiLanguageSupportDefaultLanguage] = useState<MeetingLanguage>(defaultLanguage)

  const { languageOptions } = useLanguageOptions()

  const { tracked } = useTracking()
  const { hasQuota } = useZocksQuota()
  const hasMultiLanguageSupportQuota = hasQuota('ai.multiLanguageSupport.value')
  const [showLanguageSettingModal, setShowLanguageSettingModal] = useState<boolean>(false)
  const [showDisableLanguageSettingModal, setShowDisableLanguageSettingModal] = useState<boolean>(false)
  const [showMultiLanguageEnabledToast, setShowMultiLanguageEnabledToast] = useState(false)
  const [toastText, setToastText] = useState('')
  const [justEnabledFromModal, setJustEnabledFromModal] = useState(false)

  const isMultiLanguageSupportEnterpriseManaged = React.useMemo(() => account?.enterpriseConfig.multiLanguageSupport != null, [account])

  const _multiLanguageSupportEnabledChanged = async (checked: boolean) => {
    if (checked) {
      setShowLanguageSettingModal(true)
    } else {
      setShowDisableLanguageSettingModal(true)
    }
  }

  const multiLanguageSupportEnabledChanged = tracked(_multiLanguageSupportEnabledChanged, {
    eventName: TrackEvent.MULTI_LANGUAGE_SETTING_CHANGED,
    eventDetails: {
      accountId: accountId,
      multiLanguageSetting: multiLanguageSupportEnabled,
    },
  })

  const handleLanguageChange = async () => {
    if (!account) {
      return
    }
    const accountConfig = {
      ...account.config,
      multiLanguageSupport: {
        ...account.config?.multiLanguageSupport,
        enabled: multiLanguageSupportEnabled,
        defaultLanguage: multiLanguageSupportDefaultLanguage,
      },
    }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    }).then(() => {
      setToastText('Fallback option changed')
      setShowMultiLanguageEnabledToast(true)
    })
  }

  const defaultLanguageChanged = tracked(handleLanguageChange, {
    eventName: TrackEvent.MULTI_LANGUAGE_SETTING_FALLBACK_OPTION_CHANGED,
    eventDetails: {
      accountId: accountId,
      defaultLanguage: multiLanguageSupportDefaultLanguage,
    },
  })

  const isAutoDetect = multiLanguageSupportDefaultLanguage === MeetingLanguage.AUTO_DETECT
  const selectedLanguageOption = languageOptions.find((lang) => lang.id === multiLanguageSupportDefaultLanguage) || languageOptions[0]

  const hideLanguageSettingsModal = () => {
    setShowLanguageSettingModal(false)
  }

  const hideDisableLanguageSettingsModal = () => {
    setShowDisableLanguageSettingModal(false)
  }

  const handleDisableMultiLanguage = () => {
    setMultiLanguageSupportEnabled(false)
    hideDisableLanguageSettingsModal()
    setToastText('Multi language support disabled')
    setShowMultiLanguageEnabledToast(true)
  }

  const handleEnableMultiLanguage = (selectedLanguage: MeetingLanguage) => {
    setMultiLanguageSupportEnabled(true)
    setMultiLanguageSupportDefaultLanguage(selectedLanguage)
    setJustEnabledFromModal(true)
    hideLanguageSettingsModal()
    setToastText('Multi language support enabled')
    setShowMultiLanguageEnabledToast(true)
  }

  return (
    <Container fluid className='p-0'>
      {showLanguageSettingModal ? (
        <LanguageSettingModal onHide={hideLanguageSettingsModal} onEnableClicked={(selectedLanguage) => handleEnableMultiLanguage(selectedLanguage)} />
      ) : null}
      {showDisableLanguageSettingModal ? (
        <DisableLanguageSettingModal onHide={hideDisableLanguageSettingsModal} onDisableClicked={handleDisableMultiLanguage} />
      ) : null}
      <div className='d-flex flex-column gap-4'>
        <h4 className='mb-0'>Multi language support</h4>
        {isMultiLanguageSupportEnterpriseManaged ? (
          <EnterpriseManagedBanner message='Multi language support settings are managed at the enterprise level and cannot be modified here.' />
        ) : null}
        <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <div className='d-flex align-self-center gap-3 align-items-center'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <div className='d-flex flex-column'>
                <span>
                  Capture meetings in multiple languages. Zocks automatically uses the client’s preferred language or lets you choose the meeting language
                  manually.
                  <a
                    className='button-link-medium p-0 zocks-text-gray-900 text-nowrap'
                    href='https://help.zocks.io/en/articles/13045254-manage-multilanguage-meetings'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Learn more
                  </a>
                </span>
              </div>
            </div>
            {!hasMultiLanguageSupportQuota ? (
              <ScopedBlock
                scope={{
                  scope: 'account:update',
                  superScope: ['account:admin'],
                }}
                quota={{
                  quota: 'ai.multiLanguageSupport.value',
                }}
                missingScopeAction={MissingScopeAction.UPGRADE}
              >
                <Button variant='primary'>Upgrade</Button>
              </ScopedBlock>
            ) : null}
          </Col>
        </Row>
        {hasMultiLanguageSupportQuota ? (
          <div>
            <div className='bold paragraph-medium pb-2'>When enabled:</div>
            <ul className='mb-0'>
              <li>
                Zocks determines meeting language <strong> based on your clients' language</strong> preferences.
              </li>
              <li>
                <strong>Set a fallback option</strong> when clients' languages don't indicate a common language.
              </li>
              <li>
                You can manually set the language <strong>before meetings starts</strong>.
              </li>
              <li>Follow-up emails are generated in the meeting language.</li>
              <li>Sentiment analysis only works for English meetings.</li>
            </ul>
          </div>
        ) : null}
        <div className='d-flex flex-column gap-3'>
          <div className='d-flex align-items-center gap-3'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              quota={{
                quota: 'ai.multiLanguageSupport.value',
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Form.Switch
                disabled={isAccountFetching || isMultiLanguageSupportEnterpriseManaged}
                checked={multiLanguageSupportEnabled}
                onChange={(e) => multiLanguageSupportEnabledChanged(e.currentTarget.checked)}
              />
            </ScopedBlock>
            <span className='paragraph-medium zocks-text-gray-900'>Enable multiple languages</span>
          </div>
        </div>
        {multiLanguageSupportEnabled && hasMultiLanguageSupportQuota ? (
          <div className='d-flex flex-column gap-3 border zocks-border-0.75 p-4 zocks-border-color-gray-400'>
            <p className='paragraph-large bold mb-0'>Fallback option</p>
            <p className='paragraph-medium mb-0'>
              Zocks determines meeting language based on this fallback option <strong>when client preferences don't indicate a common language</strong>.
            </p>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              quota={{
                quota: 'ai.multiLanguageSupport.value',
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <div className='d-flex flex-column gap-2'>
                <div className='d-flex flex-column'>
                  <Form.Check
                    type='radio'
                    id='default-language-option'
                    name='fallback-language-option'
                    label='Fallback language'
                    checked={!isAutoDetect}
                    onChange={() => {
                      setMultiLanguageSupportDefaultLanguage(languageOptions[0]?.id ?? MeetingLanguage.ENGLISH)
                      setJustEnabledFromModal(false)
                    }}
                    className='paragraph-medium'
                    disabled={isAccountFetching}
                  />
                </div>
                <div className='ps-4'>
                  <SelectDropdown
                    options={languageOptions}
                    initialSelectedOption={selectedLanguageOption}
                    onOptionSelected={(option) => {
                      setMultiLanguageSupportDefaultLanguage(option.id as MeetingLanguage)
                      setJustEnabledFromModal(false)
                    }}
                    disabled={isAccountFetching || isAutoDetect}
                  />
                </div>
              </div>
              <div className='d-flex flex-column gap-2 mt-2'>
                <div className='d-flex flex-column'>
                  <Form.Check
                    type='radio'
                    id='auto-detect-option'
                    name='fallback-language-option'
                    label='Auto-detect'
                    checked={isAutoDetect}
                    onChange={() => {
                      setMultiLanguageSupportDefaultLanguage(MeetingLanguage.AUTO_DETECT)
                      setJustEnabledFromModal(false)
                    }}
                    className='paragraph-medium'
                    disabled={isAccountFetching}
                  />
                </div>
                <div className='ps-4'>
                  <p className='paragraph-small mb-0'>
                    AI Assistant will detect the spoken language during the meeting. If detection is wrong,
                    <strong>your transcription will be inaccurate</strong>. Since Zocks produces <strong>no recordings</strong>, after the meeting,{' '}
                    <strong>transcriptions cannot be recreated.</strong>
                  </p>
                </div>
              </div>
            </ScopedBlock>
            {multiLanguageSupportDefaultLanguage !== defaultLanguage && !justEnabledFromModal ? (
              <div className='d-flex align-items-center justify-content-between pe-2 paragraph-small zocks-text-gray-700'>
                <div>
                  <i className='bi bi-info-square pe-2'></i>
                  This will affect upcoming meetings where Zocks uses your fallback language.
                </div>
                <Button variant='primary' className='align-self-end' onClick={defaultLanguageChanged} loading={isAccountLoading}>
                  Save Changes
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {showMultiLanguageEnabledToast ? (
        <SummaryToast
          variant={'success'}
          show={showMultiLanguageEnabledToast}
          onClose={() => {
            setShowMultiLanguageEnabledToast(false)
          }}
        >
          <i className='bi bi-check-circle-fill' />
          <span className='paragraph-medium'>{toastText}</span>
        </SummaryToast>
      ) : null}
    </Container>
  )
}
